<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { DatePicker, FilterModal, IconImage, ThemeProvider } from '@/shared/ui';
  import { formatDateForApi, parseCivilDate } from '@/shared/lib';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';

  interface UserFilters {
    admissionDate?: string;
    dismissalDate?: string;
    status?: 'ACTIVE' | 'INACTIVE';
  }

  const props = defineProps<{
    currentFilters?: UserFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: UserFilters];
    'clear-filters': [];
  }>();

  const admissionDate = ref<Date | null>(null);
  const dismissalDate = ref<Date | null>(null);
  const status = ref<'' | 'ACTIVE' | 'INACTIVE'>('');
  const admissionDatePickerOpen = ref(false);
  const dismissalDatePickerOpen = ref(false);

  function formatDate(date: Date | null): string {
    if (!date) return 'Selecione uma data';
    return date.toLocaleDateString('pt-BR');
  }

  function toDateString(date: Date | null): string | undefined {
    if (!date) return undefined;
    return formatDateForApi(date) ?? undefined;
  }

  function loadFilters(filters?: UserFilters) {
    admissionDate.value = parseCivilDate(filters?.admissionDate);
    dismissalDate.value = parseCivilDate(filters?.dismissalDate);
    status.value = filters?.status ?? '';
  }

  watch(
    () => props.currentFilters,
    (newFilters) => loadFilters(newFilters),
    { immediate: true, deep: true }
  );

  const handleApplyFilters = () => {
    const filters: UserFilters = {};
    const ad = toDateString(admissionDate.value);
    const dd = toDateString(dismissalDate.value);
    if (ad) filters.admissionDate = ad;
    if (dd) filters.dismissalDate = dd;
    if (status.value) filters.status = status.value;
    emit('update:filter', filters);
  };

  const handleClearFilters = () => {
    admissionDate.value = null;
    dismissalDate.value = null;
    status.value = '';
    emit('update:filter', {});
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-md"
    :buttons-text="{ clear: 'Limpar filtro', apply: 'Aplicar filtro' }"
    @close="emit('close')"
    @apply-filters="handleApplyFilters"
    @clear-filters="handleClearFilters"
  >
    <template #content>
      <ThemeProvider>
        <div class="p-3">
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Data de admissão:
              </h4>
              <button
                type="button"
                class="flex items-center gap-2 bg-white-01 px-4 py-2.5 border border-gray-02 rounded-lg text-gray-07 text-sm focus:outline-none focus:border-gray-07 w-full min-w-70"
                @click="admissionDatePickerOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-4.5 w-4.5"
                />
                {{ formatDate(admissionDate) }}
              </button>
              <DatePicker
                :selected-date="admissionDate"
                :is-open="admissionDatePickerOpen"
                :default-to-today="false"
                @update:selected-date="admissionDate = $event"
                @update:is-open="admissionDatePickerOpen = $event"
              />
            </div>

            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Data de demissão:
              </h4>
              <button
                type="button"
                class="flex items-center gap-2 bg-white-01 px-4 py-2.5 border border-gray-02 rounded-lg text-gray-07 text-sm focus:outline-none focus:border-gray-07 w-full min-w-70"
                @click="dismissalDatePickerOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-4.5 w-4.5"
                />
                {{ formatDate(dismissalDate) }}
              </button>
              <DatePicker
                :selected-date="dismissalDate"
                :is-open="dismissalDatePickerOpen"
                :default-to-today="false"
                @update:selected-date="dismissalDate = $event"
                @update:is-open="dismissalDatePickerOpen = $event"
              />
            </div>

            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Status:
              </h4>
              <div class="flex items-center gap-6">
                <label class="flex items-center gap-2 text-sm text-gray-07">
                  <input
                    v-model="status"
                    type="radio"
                    name="user-status"
                    class="h-4 w-4 accent-gray-07"
                    :value="'ACTIVE'"
                  >
                  Ativo
                </label>
                <label class="flex items-center gap-2 text-sm text-gray-07">
                  <input
                    v-model="status"
                    type="radio"
                    name="user-status"
                    class="h-4 w-4 accent-gray-07"
                    :value="'INACTIVE'"
                  >
                  Inativo
                </label>
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
